/**
 * Wire state → presentation.
 *
 * `CANDIDATE_STATE_PRESENTATION` deliberately has no default branch, so that adding a
 * state without deciding how it looks is a compile error. That guarantee holds inside the
 * type system; it cannot hold here, because the state arrives from HTTP as a plain string.
 *
 * So the boundary is explicit. An unrecognised state renders as 'attention' with the raw
 * value shown, rather than falling back to something neutral or success-shaped: if the
 * backend has grown a state this bundle does not know about, the honest presentation is
 * "something is here that I cannot interpret", not a reassuring grey pill.
 */

import { CANDIDATE_STATE_PRESENTATION, type CandidateState } from '@tariq/shared';

export interface Presentation {
  label: string;
  tone: 'neutral' | 'progress' | 'attention' | 'success';
}

function isKnownState(state: string): state is CandidateState {
  return Object.prototype.hasOwnProperty.call(CANDIDATE_STATE_PRESENTATION, state);
}

export function presentationFor(state: string): Presentation {
  if (isKnownState(state)) return CANDIDATE_STATE_PRESENTATION[state];
  return { label: state, tone: 'attention' };
}
